import React, { Fragment } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { debounce } from 'lodash';
import tokens from '@spotify-internal/encore-foundation';
import {
  Type,
  ButtonPrimary,
  FormGroup,
  FormInput,
  IconCheckAlt,
  FormHelpText,
} from '@spotify-internal/encore-creator-web';
import { LoadingIndicator } from '../../Shared/LoadingIndicator';
import { ERROR, LOADING, SUCCESS } from '../../../store/selectors';
import { PODCAST_SUPPORT_EMAIL } from '../../../lib/constants';
import { PodcastModalBody } from '../../Shared/PodcastModal';
import { AddPodcastModalContext } from '../AddPodcastModal/context';
import { FormattedMessage } from 'react-intl';

enum STEPS {
  CONFIRM_EMAIL,
  ENTER_CODE,
}

const ClaimPodcastHelp = styled.div`
  margin-top: ${tokens.spacer32};
  max-width: 800px;
`;

const StyledSuccess = styled.div`
  margin-top: ${tokens.spacer16};
  display: flex;
  align-items: center;
`;

const CheckMark = styled(IconCheckAlt)`
  margin-right: ${tokens.spacer8};
`;

const ResendButton = styled.button`
  cursor: pointer;
  background-color: transparent;
  border: 0;
  color: inherit;
  line-height: 1;
  letter-spacing: inherit;
  padding: 0;
  text-decoration: underline;
  &:focus {
    outline: 0;
  }
  &:hover {
    color: #1ed760;
  }
`;

interface Props {
  redactedEmailAddress: string;
  showId: string;
  sendVerificationEmail(showId: string, claimExistingShow?: boolean): void;
  sendVerificationEmailStatus?: 'ERROR' | 'LOADING' | 'SUCCESS';
  checkVerificationCode(
    showId: string,
    verificationCode: string,
    claimExistingShow?: boolean,
  ): void;
  checkVerificationCodeStatus?: 'ERROR' | 'LOADING' | 'SUCCESS';
  resetVerificationCodeStatus(): void;
  isSoundtrapPodcast?: boolean;
  onVerificationSuccess(verificationCode: string): void;
  claimExistingShow?: boolean;
}

interface State {
  currentStep: STEPS;
  verificationCode: string;
}

export class ClaimPodcast extends React.Component<Props, State> {
  static contextType = AddPodcastModalContext;
  context!: React.ContextType<typeof AddPodcastModalContext>;

  state = {
    currentStep: STEPS.CONFIRM_EMAIL,
    verificationCode: '',
  };

  componentDidMount() {
    this.context.setNextButtonDisabled(true);
  }

  componentWillUnmount() {
    this.props.resetVerificationCodeStatus();
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.sendVerificationEmailStatus === LOADING &&
      this.props.sendVerificationEmailStatus === SUCCESS
    ) {
      this.setState({ currentStep: STEPS.ENTER_CODE });
    }
    if (
      prevProps.checkVerificationCodeStatus === LOADING &&
      this.props.checkVerificationCodeStatus === SUCCESS
    ) {
      this.props.onVerificationSuccess(this.state.verificationCode);
      this.context.setNextButtonDisabled(false);
    }
  }

  onChangeWithDebouncedVerification = async e => {
    const verificationCode = e.target.value.trim();
    /* istanbul ignore next */
    if (verificationCode.length === 8) {
      this.setState({ verificationCode });
      this.debouncedVerification(this.props.showId, verificationCode, this.props.claimExistingShow);
    }
  };

  handleSendCodeClick = () => {
    this.props.sendVerificationEmail(this.props.showId, this.props.claimExistingShow);
  };

  handleResendCodeClick = () => {
    this.props.resetVerificationCodeStatus();
    this.setState({ currentStep: STEPS.CONFIRM_EMAIL });
  };

  debouncedVerification = debounce(this.props.checkVerificationCode, 500);

  renderConfirmEmail = () => {
    const instructions = this.props.isSoundtrapPodcast ? (
      <FormattedMessage
        id="add-podcast.claim-soundtrap-instructions"
        defaultMessage="We’ll send an 8-digit code to the email address associated with your show."
      />
    ) : (
      <FormattedMessage
        id="add-podcast.claim-instructions"
        defaultMessage="We’ll send an 8-digit code to the email address associated with your RSS feed."
      />
    );
    const support = this.props.isSoundtrapPodcast ? (
      <FormattedMessage
        id="add-podcast.claim-soundtrap-support"
        defaultMessage="To update your email address, contact <a>Soundtrap support</a>."
        values={{
          a: (...chunks) => (
            <a
              href="https://soundtrap.zendesk.com/hc/en-us/requests/new"
              target="_blank"
              rel="noopener noreferrer"
            >
              {chunks}
            </a>
          ),
        }}
      />
    ) : (
      <FormattedMessage
        id="add-podcast.claim-support"
        defaultMessage="To update your email address, contact your host or aggregator."
      />
    );
    return (
      <div data-testid="claim-podcast-verify-email">
        <Type as="h1" variant={Type.heading2}>
          <FormattedMessage
            id="add-podcast.claim-verify-title"
            defaultMessage="Verify that you own this podcast"
          />
        </Type>
        <Type as="p" variant={Type.body1}>
          {instructions}
        </Type>
        <Type as="p" variant={Type.body1} weight={'bold'}>
          {this.props.redactedEmailAddress}
        </Type>
        <ButtonPrimary onClick={this.handleSendCodeClick}>Send code</ButtonPrimary>
        <ClaimPodcastHelp>
          <Type as="p" variant={Type.body2} color={'gray60'} style={{ marginTop: '16px' }}>
            {support}
          </Type>
          {/* istanbul ignore next */ !this.props.isSoundtrapPodcast && (
            <Type as="p" variant={Type.body2} color={'gray60'} style={{ marginTop: '16px' }}>
              <FormattedMessage
                id="add-podcast.claim-support-note"
                defaultMessage="If you don’t recognize the email address, please contact
              <a>{PODCAST_SUPPORT_EMAIL}</a>. Include the
              name of your podcast, the link to your RSS feed and the email address of your Spotify
              account."
                values={{
                  a: (...chunks) => <a href={`mailto:${PODCAST_SUPPORT_EMAIL}`}>{chunks}</a>,
                  PODCAST_SUPPORT_EMAIL: PODCAST_SUPPORT_EMAIL,
                }}
              />
            </Type>
          )}
        </ClaimPodcastHelp>
      </div>
    );
  };

  renderEnterCode = () => (
    <div data-testid="claim-podcast-enter-code">
      <Type as="h1" variant={Type.heading2}>
        <FormattedMessage
          id="add-podcast.claim-check-email-title"
          defaultMessage="Check your email"
        />
      </Type>
      <Type as="p" variant={Type.body1}>
        <FormattedMessage
          id="add-podcast.claim-check-email-instructions"
          defaultMessage="We sent you an 8-digit code. Paste it below to finish adding your podcast. {linebreak}
        It will expire in <strong>1 hour</strong>, so come back soon."
          values={{
            linebreak: <br />,
            strong: (...chunks) => <strong>{chunks}</strong>,
          }}
        />
      </Type>

      <FormGroup
        label={
          <FormattedMessage
            id="add-podcast.claim-verification-code-title"
            defaultMessage="Verification code"
          />
        }
        labelFor="verification_code"
      >
        <FormInput
          id="verification_code"
          data-testid="verification-code-field"
          type="text"
          placeholder=""
          onChange={this.onChangeWithDebouncedVerification}
          disabled={
            this.props.checkVerificationCodeStatus === LOADING ||
            this.props.checkVerificationCodeStatus === SUCCESS
          }
          style={{ width: '148px' }}
        />
      </FormGroup>
      {this.renderVerificationCodeFeedback()}
    </div>
  );

  renderVerificationCodeFeedback = () => {
    switch (this.props.checkVerificationCodeStatus) {
      case LOADING:
        return <LoadingIndicator style={{ marginTop: '16px', justifyContent: 'start' }} />;
      case SUCCESS:
        return (
          <StyledSuccess>
            <CheckMark color="spotifyGreenHighlight" aria-label="IconCheckAlt" iconSize={16} />
            <Type
              as="p"
              data-testid="verification-code-valid-indicator"
              color="spotifyGreenHighlight"
              variant={Type.body1}
              condensed
            >
              <FormattedMessage
                id="add-podcast.claim-verification-success"
                defaultMessage="You're verified! Let's continue."
              />
            </Type>
          </StyledSuccess>
        );
      case ERROR:
        return (
          <Fragment>
            <FormHelpText error data-testid="verification-code-error-indicator">
              <FormattedMessage
                id="add-podcast.claim-verification-failure"
                defaultMessage="Invalid code. Try again or request a new one."
              />
            </FormHelpText>
            <ResendButton onClick={this.handleResendCodeClick}>
              <FormattedMessage
                id="add-podcast.claim-verification-resend-1"
                defaultMessage="Resend code"
              />
            </ResendButton>
            <Type as="p" variant={Type.body1}>
              <FormattedMessage
                id="add-podcast.claim-verification-resend-help-1"
                defaultMessage="<a>Need help?</a>"
                values={{
                  a: (...chunks) => <Link to="/faq">{chunks}</Link>,
                }}
              />
            </Type>
          </Fragment>
        );
      default:
        return (
          <Fragment>
            <ResendButton onClick={this.handleResendCodeClick}>
              <FormattedMessage
                id="add-podcast.claim-verification-resend-2"
                defaultMessage="Resend code"
              />
            </ResendButton>
            <Type as="p" variant={Type.body1}>
              <FormattedMessage
                id="add-podcast.claim-verification-resend-help-2"
                defaultMessage="<a>Need help?</a>"
                values={{
                  a: (...chunks) => <Link to="/faq">{chunks}</Link>,
                }}
              />
            </Type>
          </Fragment>
        );
    }
  };

  renderContents = () => {
    switch (this.state.currentStep) {
      case STEPS.CONFIRM_EMAIL: {
        return this.renderConfirmEmail();
      }
      case STEPS.ENTER_CODE: {
        return this.renderEnterCode();
      }
    }
  };
  render() {
    return <PodcastModalBody>{this.renderContents()}</PodcastModalBody>;
  }
}
